import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * Tab item interface definition
 */
export interface TabItem {
  id: string;
  label: string;
}

/**
 * Tabs navigation configuration interface
 */
export interface TabsNavigationConfig {
  tabs: TabItem[];
  activeTab?: string;
}

/**
 * Reusable tabs navigation component
 */
@Component({
  selector: 'caixa-webcomponent-tabs-navigation',
  standalone: true,
  templateUrl: './tabs-navigation.component.html',
  styleUrls: ['./tabs-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabsNavigationComponent {
  /**
   * Input configuration for tabs navigation
   */
  @Input()
  public config: TabsNavigationConfig = { tabs: [] };
  /**
   * Input for currently active tab
   */
  @Input()
  public activeTab = '';
  /**
   * Output event emitter for tab changes
   */
  @Output()
  public tabChange = new EventEmitter<string>();

  /**
   * Getter for the currently active tab ID
   */
  public get activeTabId(): string {
    return this.activeTab || this.config.activeTab || this.config.tabs[0]?.id || '';
  }

  /**
   * Set the active tab and emit change event
   * 
   * @param tabId - ID of the tab to be activated
   * @returns void
   */
  public setActiveTab(tabId: string): void {
    if (this.activeTabId === tabId) return;
    
    this.activeTab = tabId;
    this.tabChange.emit(tabId);
  }

  /**
   * Check if a tab is currently active
   * 
   * @param tabId - ID of the tab to check
   * @returns boolean indicating if the tab is active
   */
  public isTabActive(tabId: string): boolean {
    return this.activeTabId === tabId;
  }
}
